import React, { useEffect, useState } from "react";
import ReportRenderer from "../../components/Reports";
import { useParams } from "react-router-dom";
import useThemeContext from "../../hooks/useThemeSwitch";
import { setRootContainerWidth } from "../../reducers/layout/layoutReducer";
import { useStyles } from "./styles";
import useHasPermissions from "../../hooks/useHasPermissions";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import apiClient from "../../auth/apiClient"
import { useSelector, shallowEqual } from "react-redux";
import ValidationAccountService from "../../services/ValidationAccountService";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { LinearProgress } from "@material-ui/core";
import { USER_TYPES }  from "../../constants";

const ReportsPage = () => {
  const classes = useStyles();
  const { reportName } = useParams();
  const { layoutReducer } = useThemeContext();
  const [, layoutDispatch] = layoutReducer;
  const { hasPermissions } = useHasPermissions();
  const hasReportPermission = hasPermissions([reportName]);
  const FPAFeatureFlag = useFeatureFlag("Flexible Parking Account");
  const sharedAcctFeatureFlag = useFeatureFlag("Shared Accounts");
  const hotelIntegration = useFeatureFlag("Hotel Integrations");
  const ReportQueueFlag = useFeatureFlag("Features.Report Queue");
  
  const validationAccountService = new ValidationAccountService(apiClient);
  const currentUserId = useSelector((state) => state.user.UserID);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const { facilityID } = useCurrentFacility();
  const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityID);
  const [isOnePassActiveForUser, setIsOnePassActiveForUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const featureFlagPermission = (name) => {
    switch (name) {
      case "HotelAccessHolders":
        return hotelIntegration;
      case "HotelCharges":
        return hotelIntegration;
      case "FPAStatement":
        return FPAFeatureFlag;
      case "SharedAccountActivity":
        return sharedAcctFeatureFlag;
      default:
        return true;
    }
  };

  useEffect(() => {
    const checkOnePassEnabledStatus = async () => {
      if (reportName === "OnePassActivePasses") {
        try {
          const response = await validationAccountService.getValidationAccountsForUser(
            scopeAwareFacilityID,
            currentUserId,
            true
          );
          const contractHolderIDs = response?.data.map(x => x.contractHolderID);
  
          const responses = await Promise.all(
            contractHolderIDs.map(account =>
              validationAccountService.getValidationAccountOnePassDetails(account, scopeAwareFacilityID)
            )
          );
  
          setIsOnePassActiveForUser(responses.some(res => res.data?.active));
        } catch (err) {
          console.error("Error getting user OnePass enabled status:", err);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    
    setIsLoading(true);
    checkOnePassEnabledStatus();
  }, [reportName, scopeAwareFacilityID, currentUserId]);

  useEffect(() => {
    layoutDispatch({ type: setRootContainerWidth, payload: false });
  }, [layoutDispatch]);

  const isFeatureEnabled = featureFlagPermission(reportName);
  const canUserViewReport = currentUser.UserType === USER_TYPES.ValidationAccount && reportName === "OnePassActivePasses" ? isOnePassActiveForUser : hasReportPermission;
  const shouldRenderReport = isFeatureEnabled && canUserViewReport;
  const shouldShowPermissionError = isFeatureEnabled && !canUserViewReport;

  return (
    isLoading ? (
      <LinearProgress className={classes.loadingProgress} data-testid="loading-progress" />
    ) : (
      <>
        {!isFeatureEnabled && <h1>{reportName} is disabled</h1>}
        {shouldShowPermissionError && (
          <h1>You do not have permission to view {reportName}</h1>
        )}
        {shouldRenderReport && (
          <ReportRenderer
            className={classes.reportViewer}
            reportName={reportName}
            reportQueueEnabled={ReportQueueFlag}
          />
        )}
      </>
    )
    );
};

export default ReportsPage;
